class CompareProduct {
  constructor() {
    this.storageKey = 'compare-products';
    this.products = [];
    this.barProductNodes = {};
    this.compareProductNodes = {};

    this.addedClass = 'added-to-compare';
    this.selectors = {
      container: '[data-compare-popup]',
      compareContainer: '.compare-content__wrapper',
      wrapper: '.compare-card',
      item: '.product-card',
      compareButton: '.compare-button',
      compareText: '.compare-button-text',
      removeButton: '.compare-remove-button',
      comparePageBar: '.compare-bar',
      compareBar: '.compare-bar--list',
      count: '.compare-count'
    };

    this.container = document.querySelector(this.selectors.container);
    if (!this.container) return;

    this.compareContainer = this.container.querySelector(this.selectors.compareContainer);
    this.compareBar = this.container.querySelector(this.selectors.compareBar);
    this.select = this.container.querySelector('select-component');
    this.selectPosition = this.container.querySelector('.select-custom--options');

    this.products = this.loadFromStorage();
    this.rendered = false;

    this.updateMaxCompareItems();
    this.init();
  }

  loadFromStorage = () => {
    try {
      const data = JSON.parse(localStorage.getItem(this.storageKey));
      return Array.isArray(data) ? Array.from(new Set(data.filter(Boolean))) : [];
    } catch (e) {
      console.warn('[CompareProduct] Error reading localStorage', e);
      return [];
    }
  };

  saveToStorage = () => {
    try {
      this.products = Array.from(new Set(this.products.filter(Boolean)));
      localStorage.setItem(this.storageKey, JSON.stringify(this.products));
    } catch (e) {
      console.warn('[CompareProduct] Error saving localStorage', e);
    }
  };

  updateMaxCompareItems = () => {
    if (window.Theme && window.Theme.config && window.Theme.config.mqlMobile) {
      this.maxCompareItems = 2;
    } else if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
      this.maxCompareItems = 2;
    } else {
      this.maxCompareItems = 3;
    }
  };

  init = () => {
    this.bindEvents();
    this.setCompareButtonsState();
    this.updateCompareCount();

    if (this.products.length > 0) {
      this.openComparePopup();
      this.renderComparebar();
      this.setOptionState(this.select);
    }

    const debounceFn = typeof debounce === 'function' ? debounce : (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    };

    window.addEventListener(
      'resize',
      debounceFn(() => {
        this.updateMaxCompareItems();
        this.setCompareButtonsState();
        if (this.rendered) {
          this.syncHeight();
        }
      }, 250)
    );
  };

  bindEvents = () => {
    this.addEventToCompareButtons();
    this.addEventToRemoveButtons();
    this.renderCompareButton();
    this.clearAllButton();
    this.closeButton();
    this.addEventToCompareSelect();
  };

  closeButton = () => {
    addEventDelegate({
      context: this.container,
      selector: '.compare-popup__close, [data-close-compare]',
      handler: (e) => {
        e.preventDefault();
        this.closeComparePopup();
        this.rendered = false;
      }
    });
  };

  addToCompare(handle) {
    if (handle && !this.products.includes(handle)) {
      this.products.push(handle);
      this.saveToStorage();
      this.addRenderComparebar(handle);
      if (this.rendered) {
        this.addRenderCompare(handle);
      }
    }
  }

  removeFromCompare(handle) {
    if (!handle) return;
    this.products = this.products.filter(hdl => hdl !== handle);
    this.saveToStorage();

    if (this.compareBar) {
      const barItem = this.compareBar.querySelector(`[data-product-handle="${handle}"]`);
      if (barItem) {
        const card = barItem.closest(this.selectors.wrapper) || barItem;
        card.remove();
      }
    }

    if (this.compareContainer) {
      const contentItem = this.compareContainer.querySelector(`[data-product-handle="${handle}"]`);
      if (contentItem) {
        const card = contentItem.closest(this.selectors.wrapper) || contentItem;
        card.remove();
        this.syncHeight();
      }
    }

    delete this.barProductNodes[handle];
    delete this.compareProductNodes[handle];

    this.updateCompareCount();
    this.setCompareButtonsState();
    this.setOptionState(this.select);

    if (this.products.length === 0) {
      if (this.selectPosition) {
        this.selectPosition.classList.add('select-custom--options-top');
      }
      this.rendered = false;
      this.closeComparePopup();
    }
  }

  clearAllButton = () => {
    addEventDelegate({
      context: this.container,
      selector: '[data-compare-clear]',
      handler: (e) => {
        e.preventDefault();
        this.products = [];
        this.barProductNodes = {};
        this.compareProductNodes = {};
        this.saveToStorage();
        this.updateCompareCount();
        this.setCompareButtonsState();
        this.setOptionState(this.select);

        if (this.compareBar) this.compareBar.innerHTML = '';
        if (this.compareContainer) this.compareContainer.innerHTML = '';

        this.closeComparePopup();
        if (this.selectPosition) {
          this.selectPosition.classList.add('select-custom--options-top');
        }
        this.rendered = false;
      }
    });
  };

  setCompareButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.compareButton);
    buttons.forEach(btn => {
      const prodHandle = btn?.dataset.productHandle;
      if (!prodHandle) return;

      const isAdded = this.products.includes(prodHandle);
      if (isAdded) {
        btn.classList.remove('disabled');
        this.toggleButtonState(btn, true);
      } else {
        this.toggleButtonState(btn, false);
        if (this.products.length >= this.maxCompareItems) {
          btn.classList.add('disabled');
        } else {
          btn.classList.remove('disabled');
        }
      }
    });
  };

  updateCompareCount = () => {
    const size = this.products.length;
    const countElems = document.querySelectorAll(this.selectors.count);
    countElems.forEach(elem => {
      elem.textContent = size;
    });

    const compareBtn = this.container.querySelector('[data-compare-button]');
    if (compareBtn) {
      if (size > 1) {
        compareBtn.removeAttribute('disabled');
      } else {
        compareBtn.setAttribute('disabled', 'disabled');
      }
    }
  };

  addEventToCompareButtons = () => {
    addEventDelegate({
      selector: this.selectors.compareButton,
      handler: (e, btn) => {
        e.preventDefault();
        const productHandle = btn?.dataset.productHandle;
        if (!productHandle) return;

        const isAdded = this.products.includes(productHandle);
        if (isAdded) {
          this.removeFromCompare(productHandle);
        } else {
          if (this.products.length >= this.maxCompareItems) {
            return;
          }
          this.addToCompare(productHandle);
        }

        this.updateCompareCount();
        this.setCompareButtonsState();
        this.setOptionState(this.select);

        if (this.products.length === 0) {
          this.closeComparePopup();
        } else {
          this.openComparePopup();
        }
      }
    });
  };

  toggleButtonState = (btn, active) => {
    if (!btn) return;

    if (active) {
      btn.classList.add(this.addedClass);
    } else {
      btn.classList.remove(this.addedClass);
    }

    const compareText = btn.querySelector(this.selectors.compareText) || btn.querySelector('[data-revert-text]');
    if (compareText) {
      if (!compareText.dataset.originalText) {
        compareText.dataset.originalText = compareText.textContent.trim();
      }
      const revertText = compareText.dataset.revertText;
      if (revertText) {
        compareText.textContent = active ? revertText : compareText.dataset.originalText;
      }
    }

    if (btn.hasAttribute('aria-label')) {
      if (!btn.dataset.originalAriaLabel) {
        btn.dataset.originalAriaLabel = btn.getAttribute('aria-label');
      }
      const revertAria = compareText?.dataset.revertText;
      if (revertAria) {
        btn.setAttribute('aria-label', active ? revertAria : btn.dataset.originalAriaLabel);
      }
    }
  };

  addEventToRemoveButtons = () => {
    addEventDelegate({
      context: this.container,
      selector: this.selectors.removeButton,
      handler: (e, btn) => {
        e.preventDefault();
        const productHandle = btn?.dataset.productHandle;
        if (productHandle) {
          this.removeFromCompare(productHandle);
        }
      }
    });
  };

  renderComparebar = async () => {
    if (!this.compareBar || !this.products.length) return;

    const promises = this.products.map(async hdl => {
      if (this.barProductNodes[hdl]) return;
      try {
        const prodHTML = await fetchCache(`/products/${hdl}?view=compare-bar`);
        const item = document.createElement('div');
        item.classList.add('hidden', 'column', 'compare-card');
        item.innerHTML = prodHTML;
        if (item.querySelector(this.selectors.item)) {
          this.barProductNodes[hdl] = item;
        }
      } catch (err) {
        console.error(`[CompareProduct] Failed to load bar item for ${hdl}:`, err);
      }
    });

    await Promise.all(promises);

    this.compareBar.innerHTML = '';
    this.products.forEach(hdl => {
      const prodNode = this.barProductNodes[hdl];
      if (prodNode) {
        this.compareBar.appendChild(prodNode);
        prodNode.classList.remove('hidden');
      }
    });
  };

  addRenderComparebar = async (hdl) => {
    if (!this.compareBar || !hdl) return;

    try {
      let prodNode = this.barProductNodes[hdl];
      if (!prodNode) {
        const prodHTML = await fetchCache(`/products/${hdl}?view=compare-bar`);
        const item = document.createElement('div');
        item.classList.add('hidden', 'column', 'compare-card');
        item.innerHTML = prodHTML;
        if (item.querySelector(this.selectors.item)) {
          this.barProductNodes[hdl] = item;
          prodNode = item;
        }
      }

      if (prodNode && !this.compareBar.contains(prodNode)) {
        this.compareBar.appendChild(prodNode);
        prodNode.classList.remove('hidden');
      }
    } catch (err) {
      console.error(`[CompareProduct] Failed to add bar item for ${hdl}:`, err);
    }
  };

  renderCompare = async () => {
    if (!this.compareContainer || !this.products.length) return;

    const compareButton = this.container.querySelector('[data-compare-button]');
    compareButton?.classList.add('spinner-loading');

    const promises = this.products.map(async hdl => {
      if (this.compareProductNodes[hdl]) return;
      try {
        const prodHTML = await fetchCache(`/products/${hdl}?view=compare`);
        const item = document.createElement('div');
        item.classList.add('hidden', 'column', 'compare-card');
        item.innerHTML = prodHTML;
        if (item.querySelector(this.selectors.item)) {
          this.compareProductNodes[hdl] = item;
        }
      } catch (err) {
        console.error(`[CompareProduct] Failed to load compare item for ${hdl}:`, err);
      }
    });

    await Promise.all(promises);
    compareButton?.classList.remove('spinner-loading');

    this.compareContainer.innerHTML = '';
    this.products.forEach(hdl => {
      const prodNode = this.compareProductNodes[hdl];
      if (prodNode) {
        this.compareContainer.appendChild(prodNode);
        prodNode.classList.remove('hidden');
      }
    });

    if (this.selectPosition && !this.rendered) {
      this.selectPosition.classList.remove('select-custom--options-top');
    }

    this.rendered = true;
    requestAnimationFrame(() => {
      this.syncHeight();
    });
  };

  addRenderCompare = async (hdl) => {
    if (!this.compareContainer || !hdl) return;

    try {
      let prodNode = this.compareProductNodes[hdl];
      if (!prodNode) {
        const prodHTML = await fetchCache(`/products/${hdl}?view=compare`);
        const item = document.createElement('div');
        item.classList.add('hidden', 'column', 'compare-card');
        item.innerHTML = prodHTML;
        if (item.querySelector(this.selectors.item)) {
          this.compareProductNodes[hdl] = item;
          prodNode = item;
        }
      }

      if (prodNode && !this.compareContainer.contains(prodNode)) {
        this.compareContainer.appendChild(prodNode);
        prodNode.classList.remove('hidden');
      }

      this.rendered = true;
      requestAnimationFrame(() => {
        this.syncHeight();
      });
    } catch (err) {
      console.error(`[CompareProduct] Failed to add compare item for ${hdl}:`, err);
    }
  };

  syncHeight = () => {
    if (!this.container) return;
    const elements = this.container.getElementsByClassName('product-attribute');
    if (!elements.length) return;

    Array.from(elements).forEach(element => {
      element.style.height = '';
    });

    const heightGroups = new Map();
    Array.from(elements).forEach(element => {
      const count = element.getAttribute('item-count');
      if (!count) return;
      if (!heightGroups.has(count)) {
        heightGroups.set(count, []);
      }
      heightGroups.get(count).push(element);
    });

    heightGroups.forEach(group => {
      const maxHeight = Math.max(...group.map(el => el.offsetHeight));
      if (maxHeight > 0) {
        group.forEach(el => {
          el.style.height = `${maxHeight}px`;
        });
      }
    });
  };

  addEventToCompareSelect = () => {
    const selectElement = this.container.querySelector('[data-add-compare-select]');
    if (!selectElement) return;

    selectElement.addEventListener('change', (e) => {
      const handle = e?.target?.value;
      if (handle && handle !== 'null' && !this.products.includes(handle)) {
        if (this.products.length < this.maxCompareItems) {
          this.addToCompare(handle);
          this.updateCompareCount();
          this.setCompareButtonsState();
          this.setOptionState(this.select);
        }
      }
      selectElement.value = 'null';
    });
  };

  setOptionState = (element) => {
    if (!element) return;
    const optionElements = element.querySelectorAll('.select-custom--option');
    optionElements.forEach(option => {
      const prodHandle = option.dataset.value;
      if (this.products.length >= this.maxCompareItems || this.products.includes(prodHandle)) {
        option.classList.add('disabled');
      } else {
        option.classList.remove('disabled');
      }
    });

    if (this.products.length >= this.maxCompareItems) {
      element.classList.add('hidden');
    } else {
      element.classList.remove('hidden');
    }
  };

  openComparePopup = () => {
    if (!this.container || this.container.style.opacity === '1') return;
    this.container.style.removeProperty('visibility');
    this.container.style.setProperty('opacity', '1');
    setTimeout(() => {
      this.container.style.setProperty('--translate-y', '0');
    }, 50);
  };

  closeComparePopup = () => {
    if (!this.container || this.container.style.opacity === '0') return;
    this.container.style.setProperty('--translate-y', '100%');
    setTimeout(() => {
      this.container.style.setProperty('visibility', 'hidden');
      this.container.style.setProperty('opacity', '0');
      if (this.compareContainer) {
        this.compareContainer.innerHTML = '';
      }
      this.rendered = false;
    }, 350);
  };

  renderCompareButton = () => {
    addEventDelegate({
      context: this.container,
      selector: '[data-compare-button]',
      handler: (e) => {
        e.preventDefault();
        this.renderCompare();
      }
    });
  };
}

window.Theme = window.Theme || {};
Theme.CompareProduct = new CompareProduct();